import { createContext, useContext, useEffect, useMemo } from 'react';
import type { PropsWithChildren } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { FeatureName, FeatureToggle } from '@meeting-assistant/schemas';
import { configQueryOptions } from '@/lib/query/configQuery';

interface FeatureContextValue {
  toggles: FeatureToggle[];
}

const FeatureContext = createContext<FeatureContextValue | null>(null);

export function FeatureProvider({ children }: Readonly<PropsWithChildren>) {
  const { data, error } = useQuery(configQueryOptions);

  useEffect(() => {
    if (error) console.error('Failed to load feature toggles', error);
  }, [error]);

  const value = useMemo(() => data ?? { toggles: [] }, [data]);

  return (
    <FeatureContext.Provider value={value}>{children}</FeatureContext.Provider>
  );
}

function useFeatureContext(): FeatureContextValue {
  const context = useContext(FeatureContext);
  if (!context) {
    throw new Error(`Feature flags must be used within FeatureProvider`);
  }
  return context;
}

/** Returns false when a named feature is disabled or absent from the response. */
export function useFlag(name: FeatureName, fallbackValue = false): boolean {
  const flag = useFlags().find((toggle) => toggle.name === name);

  return flag?.enabled ?? fallbackValue;
}

/** Returns all feature values returned by the server. */
export function useFlags(): FeatureToggle[] {
  return useFeatureContext().toggles;
}
